import styled, {css} from "styled-components";
import {fontFamilies, radii} from "theme/tokens";
import {DayState, type DayStateValue} from "../sponsorCalendar";

/**
 * Externalized styles for {@link ./SponsorCalendar} (ui#496). Every colour is a `theme.tokens` read, so
 * the grid follows light and dark for free and no state is identified by a hardcoded hex.
 *
 * Crucially, no day state is signalled by COLOUR ALONE: each one also differs in weight, border or
 * text decoration (WCAG 1.4.1). A calendar whose only difference between "free" and "taken" is a hue is
 * unusable to the ~8% of men with a colour-vision deficiency — and this grid is how somebody spends
 * money.
 */

/**
 * The calendar's footprint, sized to the CHOICE it offers rather than to the column it sits in (ui#572).
 *
 * A booking is one sponsor for at most a week, so this grid exists to pick a start and an end a few days
 * apart. Stretched to the full width of the form it rendered ~93px days and stood taller than the rest of
 * the step put together, which reads as the most consequential decision on the page instead of one of
 * four. Capped, a day cell lands close to its own `min-height` and the grid is about as tall as it is
 * wide — a calendar, at the size calendars are.
 *
 * `max-width` rather than a fixed `width`, so the cap only ever REMOVES surplus. Measured on the local
 * stack: at a 1440px viewport the form column is 992px and the calendar went from filling it to 350×457;
 * at 390px the column is 354px, so the cap takes four pixels and a day cell goes from 42.3px to 41.7px.
 * That is why the width is derived from the day cells rather than rounded down to a tidier number — 350
 * is the smallest value that still clears the cells' own `min-height`, so the phone case gives up as
 * little as it possibly can while the desktop case gives up two thirds of its width.
 */
export const CalendarFrame = styled.div`
	width: 100%;
	/* 7 × 40px cells + 6 × 6px gaps + 2 × 16px padding + 2 × 1px border = 350px. */
	max-width: 350px;
	box-sizing: border-box;
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.card};
	padding: 16px;
`;

/** Month title flanked by the two nav buttons. */
export const MonthBar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 14px;
`;

export const MonthLabel = styled.h3`
	margin: 0;
	font-size: 16px;
	font-weight: 700;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const MonthNavButton = styled.button.attrs({type: "button"})`
	width: 36px;
	height: 36px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: ${radii.control};
	border: 1px solid ${({theme}) => theme.tokens.line};
	background: ${({theme}) => theme.tokens.card};
	color: ${({theme}) => theme.tokens.ink};
	font-size: 16px;
	line-height: 1;
	cursor: pointer;
	transition: 0.16s;

	&:hover:not(:disabled) {
		border-color: ${({theme}) => theme.tokens.blue};
	}

	&:disabled {
		color: ${({theme}) => theme.tokens.ink3};
		cursor: not-allowed;
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

const sevenColumns = css`
	display: grid;
	grid-template-columns: repeat(7, minmax(0, 1fr));
	gap: 6px;
`;

export const WeekdayRow = styled.div`
	${sevenColumns};
	margin-bottom: 6px;
`;

/**
 * A weekday heading. The short form is what is drawn; `<abbr title>` in the markup carries the full
 * name, so a screen reader is not left spelling out "Mi".
 */
export const Weekday = styled.div`
	text-align: center;
	font-family: ${fontFamilies.mono};
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: ${({theme}) => theme.tokens.ink3};

	abbr {
		text-decoration: none;
		cursor: help;
	}
`;

export const Grid = styled.div`
	${sevenColumns};
`;

/** Per-state skin. Each state differs in more than hue — see the note at the top of this file. */
const dayStateCss = {
	[DayState.Padding]: css`
		border-color: transparent;
		background: transparent;
		cursor: default;
	`,
	[DayState.Available]: css`
		border-color: ${({theme}) => theme.tokens.line};
		background: ${({theme}) => theme.tokens.card};
		color: ${({theme}) => theme.tokens.ink};

		&:hover:not(:disabled) {
			border-color: ${({theme}) => theme.tokens.blue};
			background: ${({theme}) => theme.tokens.accentTint};
		}
	`,
	[DayState.Selected]: css`
		border-color: ${({theme}) => theme.tokens.blue};
		background: ${({theme}) => theme.tokens.blue};
		color: ${({theme}) => theme.tokens.blueInk};
		font-weight: 700;
	`,
	[DayState.Pending]: css`
		/* Dashed, because "someone has paid but review could still free it" is genuinely provisional. */
		border-style: dashed;
		border-color: ${({theme}) => theme.tokens.orange};
		background: ${({theme}) => theme.tokens.goldTint};
		color: ${({theme}) => theme.tokens.ink2};
	`,
	[DayState.Booked]: css`
		border-color: ${({theme}) => theme.tokens.fieldLine};
		background: ${({theme}) => theme.tokens.field};
		color: ${({theme}) => theme.tokens.ink3};
		text-decoration: line-through;
		cursor: not-allowed;
	`,
	[DayState.OutOfWindow]: css`
		border-color: transparent;
		background: transparent;
		color: ${({theme}) => theme.tokens.ink3};
		opacity: 0.55;
		cursor: not-allowed;
	`,
} as const satisfies Record<DayStateValue, ReturnType<typeof css>>;

export const Day = styled.button.attrs({type: "button"})<{$state: DayStateValue}>`
	aspect-ratio: 1 / 1;
	min-height: 40px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	border: 1px solid transparent;
	border-radius: ${radii.control};
	font-family: ${fontFamilies.sans};
	font-size: 13.5px;
	font-variant-numeric: tabular-nums;
	cursor: pointer;
	transition: 0.14s;

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}

	${({$state}) => dayStateCss[$state]};
`;

export const Legend = styled.ul`
	list-style: none;
	margin: 14px 0 0;
	padding: 0;
	display: flex;
	flex-wrap: wrap;
	gap: 6px 16px;
`;

export const LegendItem = styled.li`
	display: inline-flex;
	align-items: center;
	gap: 7px;
	font-size: 12.5px;
	/* ink2, never ink3: this is readable text, and ink3 is the disabled/decorative tier (ui#302). */
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The legend's colour swatch. Decorative — the word beside it is what carries the meaning. */
export const LegendSwatch = styled.span<{$state: DayStateValue}>`
	width: 14px;
	height: 14px;
	flex: none;
	border: 1px solid transparent;
	border-radius: 5px;
	${({$state}) => dayStateCss[$state]};
`;

export const SelectionBar = styled.div`
	margin-top: 14px;
	padding-top: 12px;
	border-top: 1px solid ${({theme}) => theme.tokens.line};
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
`;

export const SelectionText = styled.p`
	margin: 0;
	font-size: 14px;
	font-weight: 700;
	color: ${({theme}) => theme.tokens.ink};
`;

export const SelectionHint = styled.span`
	font-size: 12.5px;
	font-weight: 400;
	color: ${({theme}) => theme.tokens.ink2};
`;
